/**
 * usePartsCounterData Hook
 *
 * Custom hook for fetching and managing parts counter performance data.
 * Includes loading/error state management and sorting functionality.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getPartsCounterPerformance,
  refreshPartsCounterData,
  PartsCounterPerformanceResponse,
} from '../services/api';
import {
  PartsCounterPerson,
  PartsCounterSummary,
  SortConfig,
  SortField,
} from '../components/forecast/PartsCounterPerformance/types';

interface UsePartsCounterDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean;
}

interface UsePartsCounterDataResult {
  persons: PartsCounterPerson[];
  summary: PartsCounterSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
}

/**
 * Transform raw API response to frontend PartsCounterPerson type
 */
function transformPerson(raw: PartsCounterPerformanceResponse['persons'][0]): PartsCounterPerson {
  return {
    employeeId: raw.employeeId,
    employeeName: raw.employeeName,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    grossTotal: raw.grossTotal,
    salesTotal: raw.salesTotal,
    costTotal: raw.costTotal,
    gpPctTotal: raw.gpPctTotal,
    transactionCount: raw.transactionCount,
    lostSalesCount: raw.lostSalesCount,
    counterRetail: raw.counterRetail,
    counterWholesale: raw.counterWholesale,
    customerParts: raw.customerParts,
    internalParts: raw.internalParts,
    warrantyParts: raw.warrantyParts,
    other: raw.other,
  };
}

/**
 * Transform API summary to frontend type
 */
function transformSummary(
  raw: PartsCounterPerformanceResponse['summary'],
  locationId: number,
  year: number,
  month: number
): PartsCounterSummary | null {
  if (!raw) return null;

  return {
    locationId,
    year,
    month,
    personCount: raw.personCount,
    totalGross: raw.totalGross,
    totalSales: raw.totalSales,
    totalCost: raw.totalCost,
    totalTransactions: raw.totalTransactions,
    totalLostSales: raw.totalLostSales,
    lastUpdated: raw.lastUpdated,
  };
}

export function usePartsCounterData({
  locationId,
  year,
  month,
  enabled,
}: UsePartsCounterDataParams): UsePartsCounterDataResult {
  const [persons, setPersons] = useState<PartsCounterPerson[]>([]);
  const [summary, setSummary] = useState<PartsCounterSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'grossTotal',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getPartsCounterPerformance(locationId, year, month);

      if (response.success && response.data) {
        const transformed = response.data.persons.map(transformPerson);
        setPersons(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load parts counter data');
        setPersons([]);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setPersons([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setPersons([]);
      setSummary(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshPartsCounterData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh parts counter data');
        return;
      }

      const response = await getPartsCounterPerformance(locationId, year, month);
      if (response.success && response.data) {
        const transformed = response.data.persons.map(transformPerson);
        setPersons(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load updated parts counter data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  const sortedPersons = useMemo(() => {
    if (!persons.length) return [];

    return [...persons].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.employeeName.localeCompare(b.employeeName);
      } else {
        const aVal = a[field] ?? 0;
        const bVal = b[field] ?? 0;
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [persons, sortConfig]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    persons: sortedPersons,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
  };
}
